import * as xlsx from 'xlsx';
import {
  ClientUtilizationScore,
  ParPointsScore,
  userClientUtilizations,
  userParPoints,
} from '../../types/AnnualReview';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

export const getNextYearDate = (currentDate: string): string => {
  const date = new Date(currentDate);
  const nextYearDate = new Date(date);
  nextYearDate.setFullYear(date.getFullYear() + 1);
  nextYearDate.setDate(nextYearDate.getDate() - 1);
  return dayjs(nextYearDate).format('YYYY-MM-DD');
};

export enum fileTypes {
  parPoints = 'Par Points',
  clientUtilization = 'Client Utilization',
}

export const readUploadFile = (
  e: any,
  fileType: fileTypes
): Promise<ParPointsScore[] | ClientUtilizationScore[]> => {
  return new Promise((resolve, reject) => {
    e.preventDefault();

    const files = (e.target as HTMLInputElement).files;
    if (!files?.length) return reject('No file selected');

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        if (!data) throw new Error('Failed to read file');

        const workbook = xlsx.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = xlsx.utils.sheet_to_json(sheet, { header: 1 });

        if (!jsonData.length) return reject('No data found in the file');

        const [headers, ...rows] = jsonData;
        const normalizedHeaders = (headers as string[]).map(
          normalizeColumnName
        );

        const config = fileConfigurations[fileType];
        const missingColumns = validateColumns(
          normalizedHeaders,
          config.requiredColumns
        );
        if (missingColumns.length) {
          return reject(
            `Missing required columns: ${missingColumns.join(', ')}`
          );
        }

        const parsedData = rows
          .filter((row: any) =>
            row.some((cell: any) => cell != null && cell !== '')
          )
          .map((row) =>
            mapRowToData(
              row as any[],
              normalizedHeaders,
              config.transformRow as (
                rowData: any
              ) => ParPointsScore | ClientUtilizationScore
            )
          );

        if (fileType === fileTypes.parPoints) {
          resolve(parsedData as ParPointsScore[]);
        } else if (fileType === fileTypes.clientUtilization) {
          resolve(parsedData as ClientUtilizationScore[]);
        } else {
          reject('Invalid file type');
        }
      } catch (error: any) {
        reject(error.message || 'Failed to process file');
      }
    };

    reader.onerror = () => reject('Failed to read file');
    reader.readAsArrayBuffer(files[0]);
  });
};

const fileConfigurations = {
  [fileTypes.parPoints]: {
    requiredColumns: ['email', 'parPointScore'],
    transformRow: (rowData: any): ParPointsScore => ({
      email: rowData.email,
      parPointScore: Number(rowData.parPointScore),
    }),
  },
  [fileTypes.clientUtilization]: {
    requiredColumns: ['email', 'clientUtilizationScore'],
    transformRow: (rowData: any): ClientUtilizationScore => ({
      email: rowData.email,
      clientUtilizationScore: convertAndCheck(rowData.clientUtilizationScore),
    }),
  },
};

const normalizeColumnName = (columnName: string): string => {
  const normalized = columnName.toLowerCase().replace(/\s+/g, '');
  switch (normalized) {
    case 'email':
    case 'emailaddress':
    case 'emails':
      return 'email';
    case 'parpointscore':
    case 'parpoints':
      return 'parPointScore';
    case 'clientutilizationscore':
    case 'clientutilization':
      return 'clientUtilizationScore';
    default:
      return columnName;
  }
};

const validateColumns = (
  headers: string[],
  requiredColumns: string[]
): string[] => {
  return requiredColumns.filter((col) => !headers.includes(col));
};

const mapRowToData = <T extends ParPointsScore | ClientUtilizationScore>(
  row: any[],
  headers: string[],
  transformRow: (rowData: any) => T
): T => {
  const rowData: Record<string, any> = {};
  headers.forEach((header, index) => {
    rowData[header] = row[index];
  });
  return transformRow(rowData);
};

const convertAndCheck = (str: string | number): number => {
  let num = Number(str);

  // check the number is float
  if (num % 1 !== 0) {
    return num * 100;
  }

  return num;
};

export const exportToExcel = (
  data: userParPoints[] | userClientUtilizations[],
  fileType: fileTypes
) => {
  let exportData: any[] = [];

  if (fileType === fileTypes.parPoints) {
    exportData = data.map((item: any) => {
      return {
        email: item.email,
        parPointScore: item.parPointScore,
      };
    });
  } else if (fileType === fileTypes.clientUtilization) {
    exportData = data.map((item: any) => {
      return {
        email: item.email,
        clientUtilizationScore: item.utilizationValue / 100,
      };
    });
  }

  const worksheet = xlsx.utils.json_to_sheet([]);

  xlsx.utils.sheet_add_json(worksheet, exportData, {
    origin: 'A01',
    skipHeader: false,
  });

  // Apply percentage formatting to the 'clientUtilizationScore' column if applicable
  if (fileType === fileTypes.clientUtilization) {
    const range = xlsx.utils.decode_range(worksheet['!ref'] || '');

    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellAddress = xlsx.utils.encode_cell({ r: row, c: 1 }); // assuming clientUtilizationScore is in column B (index 1)
      const cell = worksheet[cellAddress];
      if (cell && typeof cell.v === 'number') {
        cell.z = '0%'; // Set the cell to display as a percentage
      }
    }
  }
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(
    blob,
    `${fileType === fileTypes.parPoints ? fileTypes.parPoints : fileTypes.clientUtilization}.xlsx`
  );
};

export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const getRandomColor = (name: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
  ];
  // Use name as seed to get consistent color for same user
  const index =
    name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;
  return colors[index];
};
